import { useState } from "react";
import { useTranslation } from "react-i18next";
import { extractErrorMessage } from "@/shared/utils";
import {
  useAssignUnitMutation,
  useReturnUnitMutation,
  useWriteOffUnitMutation,
  useTransferUnitMutation,
  useBulkTransferUnitsMutation,
  useRevalueUnitMutation,
  useBatchAssignUnitsMutation,
  useBatchWriteOffUnitsMutation,
} from "../api";
import type { AssignItemDto, ReturnItemDto, WriteOffItemDto, TransferUnitDto, BulkTransferDto, RevalueUnitDto, BatchAssignRequest, BatchWriteOffRequest } from "../types";
import type { PromptState } from "./useWarehouseModals";

export interface UseWarehouseMutationsReturn {
  assigningId: number | null;
  returningId: number | null;
  writingOffId: number | null;
  transferringId: number | null;
  bulkTransferring: boolean;
  revaluingId: number | null;
  batchAssigningId: number | null;
  batchWritingOffId: number | null;
  isAssigning: boolean;
  isReturning: boolean;
  isWritingOff: boolean;
  isTransferring: boolean;
  isBulkTransferring: boolean;
  isRevaluing: boolean;
  isBatchAssigning: boolean;
  isBatchWritingOff: boolean;
  handleAssign: (id: number, dto: AssignItemDto) => Promise<void>;
  handleReturn: (id: number, dto?: ReturnItemDto) => Promise<void>;
  handleWriteOff: (id: number, dto: WriteOffItemDto) => Promise<void>;
  handleTransfer: (id: number, dto: TransferUnitDto) => Promise<void>;
  handleBulkTransfer: (dto: BulkTransferDto) => Promise<void>;
  handleRevalue: (id: number, dto: RevalueUnitDto) => Promise<void>;
  handleBatchAssign: (batchId: number, dto: BatchAssignRequest) => Promise<void>;
  handleBatchWriteOff: (batchId: number, dto: BatchWriteOffRequest) => Promise<void>;
  setPrompt: (state: PromptState | null) => void;
}

export function useWarehouseMutations(setPrompt: (state: PromptState | null) => void): UseWarehouseMutationsReturn {
  const { t } = useTranslation("WarehousePage");

  const [assignUnit] = useAssignUnitMutation();
  const [returnUnit] = useReturnUnitMutation();
  const [writeOffUnit] = useWriteOffUnitMutation();
  const [transferUnit] = useTransferUnitMutation();
  const [bulkTransferUnits] = useBulkTransferUnitsMutation();
  const [revalueUnit] = useRevalueUnitMutation();
  const [batchAssign] = useBatchAssignUnitsMutation();
  const [batchWriteOff] = useBatchWriteOffUnitsMutation();

  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [returningId, setReturningId] = useState<number | null>(null);
  const [writingOffId, setWritingOffId] = useState<number | null>(null);
  const [transferringId, setTransferringId] = useState<number | null>(null);
  const [bulkTransferring, setBulkTransferring] = useState(false);
  const [revaluingId, setRevaluingId] = useState<number | null>(null);
  const [batchAssigningId, setBatchAssigningId] = useState<number | null>(null);
  const [batchWritingOffId, setBatchWritingOffId] = useState<number | null>(null);

  const isAssigning = assigningId !== null;
  const isReturning = returningId !== null;
  const isWritingOff = writingOffId !== null;
  const isTransferring = transferringId !== null;
  const isBulkTransferring = bulkTransferring;
  const isRevaluing = revaluingId !== null;
  const isBatchAssigning = batchAssigningId !== null;
  const isBatchWritingOff = batchWritingOffId !== null;

  async function handleAssign(id: number, dto: AssignItemDto) {
    if (isAssigning) return;
    try {
      setAssigningId(id);
      const result = await assignUnit({ id, body: dto }).unwrap();
      const successText = result.request_fulfilled === true
        ? t("messages.assignFullyFulfilledText")
        : result.request_fulfilled === false
          ? t("messages.assignPartiallyFulfilledText")
          : t("messages.assignSuccessText");
      setPrompt({
        title: t("messages.assignSuccessTitle"),
        text: successText,
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.assignErrorText", t),
        variant: "error",
      });
    } finally {
      setAssigningId(null);
    }
  }

  async function handleReturn(id: number, dto?: ReturnItemDto) {
    if (isReturning) return;
    try {
      setReturningId(id);
      await returnUnit({ id, body: dto }).unwrap();
      setPrompt({
        title: t("messages.returnSuccessTitle"),
        text: t("messages.returnSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.returnErrorText", t),
        variant: "error",
      });
    } finally {
      setReturningId(null);
    }
  }

  async function handleWriteOff(id: number, dto: WriteOffItemDto) {
    if (isWritingOff) return;
    try {
      setWritingOffId(id);
      await writeOffUnit({ id, body: dto }).unwrap();
      setPrompt({
        title: t("messages.writeOffSuccessTitle"),
        text: t("messages.writeOffSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.writeOffErrorText", t),
        variant: "error",
      });
    } finally {
      setWritingOffId(null);
    }
  }

  async function handleTransfer(id: number, dto: TransferUnitDto) {
    if (isTransferring) return;
    try {
      setTransferringId(id);
      await transferUnit({ id, body: dto }).unwrap();
      setPrompt({
        title: t("messages.transferSuccessTitle"),
        text: t("messages.transferSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.transferErrorText", t),
        variant: "error",
      });
    } finally {
      setTransferringId(null);
    }
  }

  async function handleBulkTransfer(dto: BulkTransferDto) {
    if (isBulkTransferring) return;
    try {
      setBulkTransferring(true);
      await bulkTransferUnits(dto).unwrap();
      setPrompt({
        title: t("messages.bulkTransferSuccessTitle"),
        text: t("messages.bulkTransferSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.bulkTransferErrorText", t),
        variant: "error",
      });
    } finally {
      setBulkTransferring(false);
    }
  }

  async function handleRevalue(id: number, dto: RevalueUnitDto) {
    if (isRevaluing) return;
    try {
      setRevaluingId(id);
      await revalueUnit({ id, body: dto }).unwrap();
      setPrompt({
        title: t("messages.revalueSuccessTitle"),
        text: t("messages.revalueSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.revalueErrorText", t),
        variant: "error",
      });
    } finally {
      setRevaluingId(null);
    }
  }

  async function handleBatchAssign(batchId: number, dto: BatchAssignRequest) {
    if (isBatchAssigning) return;
    try {
      setBatchAssigningId(batchId);
      const result = await batchAssign({ batchId, body: dto }).unwrap();
      const successText = result.request_fulfilled === true
        ? t("messages.batchAssignFullyFulfilledText", { count: result.units_assigned })
        : result.request_fulfilled === false
          ? t("messages.batchAssignPartiallyFulfilledText", { count: result.units_assigned })
          : t("messages.batchAssignSuccessText", { count: result.units_assigned });
      setPrompt({
        title: t("messages.batchAssignSuccessTitle"),
        text: successText,
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.batchAssignErrorText", t),
        variant: "error",
      });
    } finally {
      setBatchAssigningId(null);
    }
  }

  async function handleBatchWriteOff(batchId: number, dto: BatchWriteOffRequest) {
    if (isBatchWritingOff) return;
    try {
      setBatchWritingOffId(batchId);
      const result = await batchWriteOff({ batchId, body: dto }).unwrap();
      setPrompt({
        title: t("messages.batchWriteOffSuccessTitle"),
        text: t("messages.batchWriteOffSuccessText", { count: result.units_written_off }),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.batchWriteOffErrorText", t),
        variant: "error",
      });
    } finally {
      setBatchWritingOffId(null);
    }
  }

  return {
    assigningId,
    returningId,
    writingOffId,
    transferringId,
    bulkTransferring,
    revaluingId,
    batchAssigningId,
    batchWritingOffId,
    isAssigning,
    isReturning,
    isWritingOff,
    isTransferring,
    isBulkTransferring,
    isRevaluing,
    isBatchAssigning,
    isBatchWritingOff,
    handleAssign,
    handleReturn,
    handleWriteOff,
    handleTransfer,
    handleBulkTransfer,
    handleRevalue,
    handleBatchAssign,
    handleBatchWriteOff,
    setPrompt,
  };
}
